import type { SliceComponentProps } from "@prismicio/react";
import {
  asText,
  asDate,
  asImagePixelDensitySrcSet,
  asImageSrc,
  asImageWidthSrcSet,
} from "@prismicio/helpers";
import { useLocale, useLocalePath } from "@commercekit/remix"
import React, { useMemo } from "react";
import WvLink from "~/components/atoms/WvLink";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { BlogContentRichTextWithBannerSlice } from "prismicio-types";

// Same compression as prod (Prismic default); fit=max never upscales originals smaller than the requested width
const imgixParams = {
  auto: ["format" as const, "compress" as const],
  fit: "max" as const,
};

// Header box per breakpoint (container width x .news-item-header height); fit=min crops to it without upscaling
const headerSizes = [
  { media: "(min-width: 1440px)", w: 1380, h: 300 },
  { media: "(min-width: 1200px)", w: 1140, h: 300 },
  { media: "(min-width: 992px)", w: 960, h: 300 },
  { media: "(min-width: 768px)", w: 720, h: 250 },
  { media: "(min-width: 576px)", w: 540, h: 250 },
];

// Images pasted as HTML in the post body: request them at their height attribute instead of the original
const resizeContentImages = (html: string) =>
  html.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = tag.match(/\ssrc="([^"]+)"/i)?.[1];
    const height = Number(tag.match(/\sheight="(\d+)"/i)?.[1]);
    if (!src?.startsWith("https://images.prismic.io/") || !height) return tag;

    const build = (dpr: number) => {
      const url = new URL(src.replace(/&amp;/g, "&"));
      url.searchParams.set("auto", "format,compress");
      url.searchParams.set("fit", "max");
      url.searchParams.set("h", String(height));
      url.searchParams.set("dpr", String(dpr));
      return url.toString().replace(/&/g, "&amp;");
    };

    return tag
      .replace(src, build(1))
      .replace(/^<img\b/i, `<img srcset="${build(1)} 1x, ${build(2)} 2x" loading="lazy"`);
  });

const BlogContentRichTextWithBanner = ({
  slice,
}: SliceComponentProps<BlogContentRichTextWithBannerSlice>) => {
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const { locale } = useLocale();

  const content = useMemo(() => {
    const iframeRegex = /<iframe[^>]*>.*?<\/iframe>/gi;
    const wrapIframe = (html: string | undefined) => {
      if (!html) return html;
      return html.replace(iframeRegex, (match) => {
        return `<div class="responsive-video">${match}</div>`;
      });
    };
    return wrapIframe(resizeContentImages(asText(slice.primary.content)));
  }, [slice.primary.content]);

  return (
    <article className="col-12 content mb-3">
      <header className="img news-item-header mb-3">
        <picture>
          {headerSizes.map(({ media, w, h }) => (
            <source
              key={media}
              media={media}
              srcSet={
                asImagePixelDensitySrcSet(slice.primary.image, {
                  ...imgixParams,
                  fit: "min",
                  w,
                  h,
                  pixelDensities: [1, 2],
                })?.srcset
              }
            />
          ))}
          {/* Phones: the box ratio follows the screen width, so resize by width only */}
          <img
            src={
              asImageSrc(slice.primary.image, { ...imgixParams, w: 828 }) || ""
            }
            srcSet={
              asImageWidthSrcSet(slice.primary.image, {
                ...imgixParams,
                widths: [480, 828, 1200],
              })?.srcset
            }
            sizes="100vw"
            alt={slice.primary.image.alt || ""}
          />
        </picture>
        <span className="date">
          <strong>
            {" "}
            {asDate(slice.primary.date_posted)?.toLocaleDateString(locale, {
              day: "2-digit",
            })}
          </strong>{" "}
          {asDate(slice.primary.date_posted)?.toLocaleDateString(locale, {
            month: "short",
          })}
        </span>
        <span className="overlay">
          {" "}
          <h1 className="heading sans-serif strong mb-3">
            {asText(slice.primary.title)}
          </h1>
          {slice?.items?.length > 0
            ? slice?.items?.map((item, i) => (
                <span key={`${item}` + i} className="label mr-1 mb-0">
                  {asText(item.title_label)}
                </span>
              ))
            : null}
        </span>
        <span className="navigation">
          <WvLink
            to={localePath("/blog/")}
            data-icon-before=""
            className="mr-lg-3"
          >
            <span className="d-none d-sm-inline-block">
              {" "}
              {$pt("blog_label_back_to_home")}
            </span>
          </WvLink>
        </span>
      </header>
      <section className="content">
        <div className="row">
          <div
            className="col-12 col-lg-10"
            dangerouslySetInnerHTML={{
              __html: content || "",
            }}
          ></div>
        </div>
      </section>
    </article>
  );
};

export default BlogContentRichTextWithBanner;
